import type { UploadSnapshot, UploadStatus } from '@krp/client-runtime'

import { clockText, momentText } from './time'

/**
 * Индикатор «учтено на сервере» (RP-OWN9): чистая функция от того, что агент сказал про свою очередь выгрузки, и от того, что
 * делал монитор чтения. Состояние всегда выражено значком И словами, не цветом. Ничего ожидаемого не изображается совершённым:
 * «доставлено» — только когда агент в последнем ответе сказал, что очередь пуста и отказов нет; отправка запроса, ответ,
 * ожидание следующей проверки и повторная проверка после сбоя различаются в строке свежести.
 */
export type DeliveryKind =
  /** Агент старый и состояние выгрузки не отдаёт. */
  | 'unsupported'
  /** Ответа ещё нет (запрос отправлен или отложен). */
  | 'checking'
  /** Агента нет на связи или он не ответил: состояние неизвестно. */
  | 'unreachable'
  | 'disabled'
  | 'delivered'
  | 'queued'
  | 'waiting-revision'
  | 'rejected'

export type DeliverySeverity = 'ok' | 'info' | 'attention' | 'problem'

/** Отказ сервера целиком: всё, что нужно администратору, и готовый текст для копирования. */
export interface DeliveryError {
  text: string
  code: string | null
  status: number | null
  requestId: string | null
  operationId: string | null
  revision: number | null
  at: string | null
  report: string
}

export interface DeliveryView {
  kind: DeliveryKind
  /** Значок состояния; в разметке скрыт от чтения, слово — в `title`. */
  mark: string
  title: string
  severity: DeliverySeverity
  /** Подробности словами. */
  lines: readonly string[]
  error: DeliveryError | null
  /** Откуда эти сведения и когда проверка в следующий раз: отправка, ответ, ожидание, повтор — раздельно. */
  freshness: string
}

const MARKS: Readonly<Record<DeliveryKind, string>> = {
  unsupported: '–',
  checking: '…',
  unreachable: '?',
  disabled: '⊘',
  delivered: '✓',
  queued: '⏳',
  'waiting-revision': '⏸',
  rejected: '✖',
}

function make(
  kind: DeliveryKind,
  title: string,
  severity: DeliverySeverity,
  lines: readonly string[],
  freshness: string,
  error: DeliveryError | null = null,
): DeliveryView {
  return { kind, mark: MARKS[kind], title, severity, lines, error, freshness }
}

const NO_REASON = 'Агент не сообщил причину отказа.'

function errorOf(status: UploadStatus): DeliveryError {
  const last = status.lastRejected
  const text = last !== null && last.text !== '' ? last.text : (status.lastError ?? NO_REASON)
  const report = [
    'Доставка в центральный журнал: запись отклонена сервером',
    `Причина: ${text}`,
    last?.code ? `Код: ${last.code}` : null,
    last?.status ? `HTTP-статус: ${last.status}` : null,
    last?.requestId ? `Номер запроса: ${last.requestId}` : null,
    last?.operationId ? `Операция: ${last.operationId}, ревизия ${last.revision}` : null,
    last?.at ? `Время: ${last.at}` : null,
  ]
    .filter((line): line is string => line !== null)
    .join('\n')
  return {
    text,
    code: last?.code ?? null,
    status: last?.status ?? null,
    requestId: last?.requestId ?? null,
    operationId: last?.operationId ? last.operationId : null,
    revision: last?.operationId ? last.revision : null,
    at: last?.at ? last.at : null,
    report,
  }
}

function queueLines(status: UploadStatus): string[] {
  if (status.pending === 0) return []
  const since = status.oldestPendingAt === null ? '' : `, самая старая с ${momentText(status.oldestPendingAt)}`
  return [`В очереди на отправку: ${status.pending}${since}.`]
}

function deliveredLine(status: UploadStatus): string {
  return status.lastSuccessAt === null ? 'С запуска агента доставок ещё не было.' : `Последняя доставка: ${momentText(status.lastSuccessAt)}.`
}

function baseOf(status: UploadStatus, freshness: string): DeliveryView {
  if (status.rejected > 0) {
    return make(
      'rejected',
      `Сервер отклонил записей: ${status.rejected}`,
      'problem',
      ['Эти записи сервером не учтены и сами не повторяются: нужен разбор администратором.', ...queueLines(status)],
      freshness,
      errorOf(status),
    )
  }
  if (status.waitingRevision > 0) {
    return make(
      'waiting-revision',
      `Сервер ждёт пропущенную ревизию: ${status.waitingRevision}`,
      'attention',
      ['Запись остаётся в очереди и уйдёт, когда дойдёт ревизия, которой не хватает. Это ещё не отказ и не принятие.', ...queueLines(status)],
      freshness,
    )
  }
  if (!status.enabled) {
    return make(
      'disabled',
      'Выгрузка выключена',
      'info',
      [
        'Адрес выгрузки в агенте не задан: операции остаются на кассе и на сервер не уходят.',
        ...queueLines(status),
        ...(status.pending > 0 ? ['Очередь копится и уйдёт, когда выгрузку включат.'] : []),
      ],
      freshness,
    )
  }
  if (status.pending > 0) {
    return make(
      'queued',
      `В очереди на отправку: ${status.pending}`,
      'attention',
      [
        ...(status.oldestPendingAt === null ? [] : [`Самая старая запись с ${momentText(status.oldestPendingAt)}.`]),
        ...(status.lastError === null ? [] : [`Причина задержки, как её назвал агент: ${status.lastError}`]),
        deliveredLine(status),
      ],
      freshness,
    )
  }
  return make(
    'delivered',
    'Учтено на сервере',
    'ok',
    ['Очередь пуста, отказов нет.', deliveredLine(status), ...(status.lastError === null ? [] : [`Последняя ошибка отправки: ${status.lastError}`])],
    freshness,
  )
}

export interface DeliveryViewInput {
  /** Агент на связи и объявил выгрузку. */
  supported: boolean
  snapshot: UploadSnapshot
}

/** Состояние, когда агента нет вовсе (окно «Доставка» открыто на компьютере без агента). */
export function deliveryViewNoAgent(): DeliveryView {
  return make(
    'unreachable',
    'Состояние доставки неизвестно',
    'attention',
    ['Кассовый агент на этом компьютере не найден или не запущен.', 'Что дошло до сервера, видно в ведомости ниже.'],
    'Проверка не выполнялась: агента нет.',
  )
}

export function deliveryViewOf({ supported, snapshot }: DeliveryViewInput): DeliveryView {
  if (!supported) {
    return make('unsupported', 'Состояние доставки недоступно', 'info', ['Этот кассовый агент не отдаёт состояние выгрузки: обновите агент.'], 'Проверка не выполнялась.')
  }
  const nextText = snapshot.nextAt === null ? '' : ` Следующая проверка в ${clockText(snapshot.nextAt)}.`
  const known = snapshot.status
  const readText = snapshot.readAt === null ? '' : clockText(snapshot.readAt)

  if (known === null) {
    if (snapshot.phase === 'failed') {
      return make(
        'unreachable',
        'Состояние доставки неизвестно',
        'attention',
        [`Агент не ответил: ${snapshot.failure ?? 'причина не названа'}`, 'Что дошло до сервера, видно в ведомости доставки.'],
        `Проверок без ответа подряд: ${snapshot.failures}.${nextText}`,
      )
    }
    if (snapshot.phase === 'deferred') {
      return make('checking', 'Проверяем доставку…', 'info', ['Проверка отложена: сейчас идёт операция с деньгами, диагностика ей не мешает.'], `Запрос агенту не отправлялся.${nextText}`)
    }
    return make('checking', 'Проверяем доставку…', 'info', ['Запрос состояния выгрузки отправлен агенту, ответа ещё нет.'], 'Ответ агента не получен.')
  }

  if (snapshot.phase === 'sending') {
    return baseOf(known, `Повторная проверка: запрос отправлен, ответа ещё нет. Показан ответ от ${readText}.`)
  }
  if (snapshot.phase === 'deferred') {
    return baseOf(known, `Проверка отложена на время операции с деньгами. Показан ответ от ${readText}.${nextText}`)
  }
  if (snapshot.phase === 'failed') {
    const base = baseOf(known, `Агент не ответил на проверку (подряд: ${snapshot.failures}). Показан ответ от ${readText}, он мог устареть.${nextText}`)
    // Отказ сервера важнее обрыва связи: он не исчезает от того, что агент замолчал.
    if (base.kind === 'rejected') return base
    return make(
      'unreachable',
      'Состояние доставки неизвестно',
      'attention',
      [`Агент не ответил: ${snapshot.failure ?? 'причина не названа'}`, `Последний известный ответ (${readText}): ${base.title}.`],
      base.freshness,
    )
  }
  return baseOf(known, `Ответ агента получен в ${readText}.${nextText}`)
}
